import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  Fragment,
} from 'react';
import {
  SidePanel,
  VerticalTabs,
  Divider,
  Box,
  CounterBadge,
  Loader,
} from '@wix/design-system';
import {
  ErrorState,
  PendingApp,
  MoreApps,
  RecommendedTools,
} from '../components';
import { TRANSLATIONS } from '../../constants/translations';
import {
  APP_MANAGER_ORIGIN,
  RECOMMENDED_APPS_TAB_ID,
  MORE_APPS_TAB_ID,
} from '../../constants/constants';
import {
  AUTOMATION_IDS,
  getBusinessAppSidebarTabDataHook,
} from '../../constants/automationIds';
import { useSiteSetup } from './hooks/useSiteSetup';
import { useRecommendedTools } from './hooks/useRecommendedTools';
import type { RegularAppData } from '../../services/appMarket/appMarket.types';
import type { DealerAppPageData } from '../../services/dealer/dealer.types';
import type { DealerSegmentContent } from '../../services/dealer/dealer.segmentContent';
import styles from './myBusiness.module.scss';

import { AppManagerDataContext } from '../../providers/AppManagerDataProvider';
import {
  EditorType,
  InstallInitiator,
  InstallationOriginType,
} from '@wix/platform-editor-sdk';
import { getAppVersionToInstall } from '../../services/appMarket/appMarket.utils';
import { BusinessApp } from './BusinessApp/BusinessApp';
import { useBILogger } from '@wix/fe-essentials/react';
import cx from 'classnames';
import { t } from '../../../../essentials/i18n';

import {
  AppActions,
  AppPremiumBannerInfo,
} from '../../services/platform/platform.types';
import { BI_EVENTS_IDS } from '../../constants/biEvents';
import { getBIReportData } from '../../utils/biEvents';
import { MyBusinessOrigin } from '../../../../types/appManager';

export interface MyBusinessViewProps {
  apps: RegularAppData[];
  moreApps: RegularAppData[];
  initialSelectedAppId?: string;
  segmentContent?: DealerSegmentContent | null;
  dealerData?: Record<string, DealerAppPageData> | null;
  origin?: MyBusinessOrigin;
}

export const MyBusiness: React.FC<MyBusinessViewProps> = ({
  apps,
  moreApps,
  initialSelectedAppId,
  segmentContent,
  dealerData,
  origin,
}) => {
  const { api } = useContext(AppManagerDataContext);
  const logger = useBILogger();

  const [selectedTabId, setSelectedTabId] = useState(() => {
    if (initialSelectedAppId) {
      return initialSelectedAppId;
    }
    if (apps.length > 0) {
      return apps[0].id;
    }
    return MORE_APPS_TAB_ID;
  });

  const [appActions, setAppActions] = useState<AppActions>();

  const [premiumBannerInfo, setPremiumBannerInfo] =
    useState<AppPremiumBannerInfo | null>();

  const [tabLoadOrigin, setTabLoadOrigin] = useState<string | undefined>(
    origin,
  );

  const [siteSetup, { isLoading: isLoadingSetup, error: setupError }] =
    useSiteSetup(
      api.metaSiteInstance,
      api.checkPermissions,
      api.openBusinessManager,
    );

  const [
    recommendedTools,
    { isLoading: isLoadingRecommendations, error: recommendationsError },
  ] = useRecommendedTools(api.metaSiteInstance, api.openBusinessManager);

  const isLoading = isLoadingSetup || isLoadingRecommendations;
  const error = setupError || recommendationsError;

  const selectedApp = apps.find((app) => app.id === selectedTabId);

  const translationKeys = useMemo(() => {
    return {
      recommendedAppsTab:
        segmentContent?.promotionalAppsTitle ||
        TRANSLATIONS.MY_BUSINESS.SIDEBAR.RECOMMENDED_APPS_TAB,
      moreAppsTab:
        segmentContent?.moreAppsTitle ||
        TRANSLATIONS.MY_BUSINESS.SIDEBAR.MORE_APPS_TAB,
    };
  }, [segmentContent]);

  const renderSidebar = () => {
    const sidebarTabs = apps.map((app, index) => ({
      id: app.id,
      label: app.name,
      positionId: index,
      showDotIndicator: app.isPending || app.isUpdateAvailable,
      showDivider: index === apps.length - 1,
      dataHook: getBusinessAppSidebarTabDataHook(app.id),
    }));

    const shouldShowRecommendedToolsTab =
      !isLoadingRecommendations && !recommendedTools.isEmpty();

    if (shouldShowRecommendedToolsTab) {
      sidebarTabs.push({
        id: RECOMMENDED_APPS_TAB_ID,
        label: t(translationKeys.recommendedAppsTab),
        positionId: sidebarTabs.length + 1,
        showDotIndicator: false,
        showDivider: false,
        dataHook: AUTOMATION_IDS.MY_BUSINESS.SIDEBAR_RECOMMENDED_TAB,
      });
    }

    sidebarTabs.push({
      id: MORE_APPS_TAB_ID,
      label: t(translationKeys.moreAppsTab),
      positionId: sidebarTabs.length + 1,
      showDotIndicator: false,
      showDivider: false,
      dataHook: AUTOMATION_IDS.MY_BUSINESS.SIDEBAR_MORE_APPS_TAB,
    });

    const getPositionIdByTabId = (tabId: string): number =>
      sidebarTabs.find((tab) => tab.id === tabId)?.positionId || 0;

    const getTabIdByPositionId = (positionId: number): string =>
      sidebarTabs.find((tab) => tab.positionId === positionId)?.id ?? '';

    return (
      <>
        <Box
          className={cx(styles.sidebar, {
            [styles.classic]: api.isClassicEditor,
          })}
          direction="vertical"
          paddingTop="SP1"
        >
          <SidePanel.Content stretchVertically noPadding>
            <VerticalTabs
              activeTabId={getPositionIdByTabId(selectedTabId)}
              size="small"
              onChange={(positionId) => {
                logger.log(
                  getBIReportData(
                    BI_EVENTS_IDS.TAB_CLICK,
                    getTabIdByPositionId(positionId),
                    {
                      origin_tab_name: selectedTabId,
                      target_tab_name: getTabIdByPositionId(positionId),
                    },
                  ),
                );
                setSelectedTabId(getTabIdByPositionId(positionId));
                setTabLoadOrigin(getTabIdByPositionId(positionId));
              }}
            >
              {sidebarTabs.map((tab) => (
                <Fragment key={tab.id}>
                  <VerticalTabs.TabItem id={tab.positionId}>
                    <Box verticalAlign="middle" dataHook={tab.dataHook}>
                      {tab.label}
                      {tab.showDotIndicator && (
                        <CounterBadge className={styles.dot} skin="danger" />
                      )}
                    </Box>
                  </VerticalTabs.TabItem>

                  {tab.showDivider && (
                    <Box
                      paddingRight="SP5"
                      paddingLeft="SP5"
                      paddingTop="SP2"
                      paddingBottom="SP2"
                    >
                      <Divider />
                    </Box>
                  )}
                </Fragment>
              ))}
            </VerticalTabs>
          </SidePanel.Content>
        </Box>

        <Divider direction="vertical" />
      </>
    );
  };

  const onAddApp = (app: RegularAppData) => {
    api.addApp({
      appDefId: app.id,
      biData: {
        origin: APP_MANAGER_ORIGIN,
      },
      platformOrigin: {
        initiator: InstallInitiator.Editor,
        type: EditorType.Classic,
        info: {
          type: InstallationOriginType.MY_BUSINESS,
        },
      },
      appVersion: getAppVersionToInstall(app),
    });
  };

  const onOpenAppMarket = () => api.openAppMarket(APP_MANAGER_ORIGIN);

  const renderLoader = () => (
    <Box width="100%" height="100%" align="center" verticalAlign="middle">
      <Loader />
    </Box>
  );

  const renderContent = () => {
    const isOpened = {
      recommendedApps: selectedTabId === RECOMMENDED_APPS_TAB_ID,
      moreApps: selectedTabId === MORE_APPS_TAB_ID,
    };

    if (error && segmentContent) {
      return <ErrorState error={error} segmentContent={segmentContent} />;
    }

    if (isLoadingSetup) {
      return renderLoader();
    }

    if (isOpened.recommendedApps) {
      return (
        <RecommendedTools
          tools={recommendedTools.getAll()}
          trigger={tabLoadOrigin}
        />
      );
    }

    if (isOpened.moreApps) {
      return (
        <MoreApps
          apps={moreApps}
          onAddApp={onAddApp}
          onOpenAppMarket={onOpenAppMarket}
          trigger={tabLoadOrigin}
        />
      );
    }

    // `premiumBannerInfo` could be `null` (valid value) - need to check explicitly for `undefined`
    if (!selectedApp || !appActions || premiumBannerInfo === undefined) {
      return null;
    }
    if (selectedApp.isInstalled || selectedApp.isInstalledOnOtherBranches) {
      return (
        <BusinessApp
          key={selectedApp.id}
          app={selectedApp}
          appSetup={siteSetup.getAppSetup(selectedApp.id)}
          recommendedTools={recommendedTools.getByApp(selectedApp.id)}
          dealerData={dealerData?.[selectedApp.id]}
          appActions={appActions}
          premiumBannerInfo={premiumBannerInfo}
          trigger={tabLoadOrigin}
          onAddApp={() => onAddApp(selectedApp)}
        />
      );
    }

    if (selectedApp.isPending) {
      return (
        <PendingApp
          onAddApp={() => onAddApp(selectedApp)}
          trigger={tabLoadOrigin}
        />
      );
    }

    return null;
  };

  const allAppsInstalled = apps
    ?.filter((app) => app.isInstalled)
    ?.map((app) => app.name)
    ?.join(',');

  const recommendedApps = recommendedTools
    ?.getAll()
    ?.map((app) => app.name)
    ?.join(',');

  useEffect(() => {
    if (selectedApp) {
      api.getAppActions(selectedApp).then((actions) => setAppActions(actions));
      api
        .getAppPremiumBannerInfo(selectedApp.id)
        .then((info) => setPremiumBannerInfo(info));
    }
  }, [selectedApp, api]);

  return (
    <div
      className={styles.container}
      data-hook={AUTOMATION_IDS.MY_BUSINESS.CONTAINER}
    >
      {apps.length ? renderSidebar() : null}

      <SidePanel width={450}>
        <SidePanel.Content stretchVertically noPadding>
          <div className={styles.content}>{renderContent()}</div>
        </SidePanel.Content>
      </SidePanel>
    </div>
  );
};
